import { useMutation } from '@apollo/client';
import { faCloudUpload, faFileAlt } from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  Card,
  ColumnDefinition,
  CustomActionWithPayload,
  FieldLabel,
  IconButton,
  LegalBody,
  MediumLightTitle,
  palettes,
  RowDefinition,
  SmallBody,
  Table,
  useFlashMessage,
  useTableState
} from '@indigo-ag/component-module';
import {
  AgFinancingDocumentType,
  AgFinancingFile,
  AgFinancingFileFolder,
  AgFinancingFilesContext,
  AgFinancingInput,
  AgFinancingMatriculaDetail,
  FieldMonitoringStatus,
  Phase2
} from '@indigo-ag/schema';
import i18next from 'core/i18n';
import React, { useContext, useState } from 'react';

import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import {
  ConfirmationDialog,
  CopyToClipboardButton,
  DocumentCard,
  GridContainer,
  GridContainerItem,
  NoResultsFound,
  biomeTypesOptions,
  evaluationTypesOptions,
  utils
} from 'shared';
import { FieldMonitoringUtil, UserUtil, date, parseNumberToLocale } from 'shared/utils';
import { ToggleOnHoldFieldMonitoringAnalysis } from '../../../shared/credit-analysis';
import { MultipleFileUploadResult, ProgressDictionary, uploadFilesToAgFinancing } from '../../../utils';
import { DeleteFileModal, UpdateDetailModal, UploadFileModal } from './ActionModals';
import styled from 'styled-components';
import VegaIntegrationStatus from './VegaIntegrationStatus/VegaIntegrationStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface FormattedMatriculasDetailData {
  id: string;
  kml?: AgFinancingFile;
  matricula?: AgFinancingFile;
  detail: AgFinancingMatriculaDetail | undefined;
  reports: AgFinancingFile[];
}

interface MatriculasListProps {
  data: FormattedMatriculasDetailData[];
  fieldMonitoringData: Phase2['fieldMonitoring'];
}

type ColumnId =
  | 'matriculaDocument'
  | 'KMLDocument'
  | 'emissionDate'
  | 'state'
  | 'totalArea'
  | 'farmName'
  | 'cultivableArea';

interface UploadType {
  rowId: string;
  isFieldMonitoring: boolean;
}

interface DeleteType {
  rowId: string;
  file?: AgFinancingFile;
  isFieldMonitoring: boolean;
}

type OpenModalAction =
  | CustomActionWithPayload<'OPEN_DELETE_FILE_MODAL', DeleteType>
  | CustomActionWithPayload<'OPEN_UPLOAD_FILE_MODAL', UploadType>
  | CustomActionWithPayload<'OPEN_DETAIL_MODAL', string>;

type DefinitionContext = {};

const CustomCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding-top: 1rem;
  width: 100%;
  height: 100%;
  gap: 0.75rem;
`;

const columnDefinition: ColumnDefinition<
  FormattedMatriculasDetailData,
  ColumnId,
  DefinitionContext,
  OpenModalAction
>[] = [
  {
    id: 'matriculaDocument',
    header: '',
    cell: ({ row }) => {
      const additionalButton = (
        <CopyToClipboardButton valueToCopy={row.id} tooltipTitle={i18next.t('copyMatriculaIdToClipboard')} />
      );
      return (
        <CustomCell>
          <FieldLabel>{i18next.t('matricula')}</FieldLabel>
          {!!row.matricula && <DocumentCard document={row.matricula} additionalButtonSlot={additionalButton} />}
        </CustomCell>
      );
    },
    constraints: {
      min: { rem: 9 },
      max: { fr: 2 }
    }
  },
  {
    id: 'KMLDocument',
    header: '',
    cell: ({ row, rowId, onTableAction }) => {
      const uploadButton = (
        <IconButton
          icon={faCloudUpload}
          onClick={() =>
            onTableAction({ type: 'OPEN_UPLOAD_FILE_MODAL', payload: { rowId, isFieldMonitoring: false } })
          }
        />
      );
      return (
        <CustomCell>
          <FieldLabel>KML {i18next.t('file')}</FieldLabel>
          {!!row.kml ? (
            <DocumentCard
              document={{ ...row.kml }}
              onDelete={() =>
                onTableAction({
                  type: 'OPEN_DELETE_FILE_MODAL',
                  payload: { rowId, isFieldMonitoring: false, file: row.kml }
                })
              }
              additionalButtonSlot={uploadButton}
            />
          ) : (
            <Card
              style={{
                height: '40px',
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingLeft: '1rem'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <FontAwesomeIcon icon={faFileAlt} color={palettes.brand.gray[500]} />
                <SmallBody
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: palettes.brand.gray[500]
                  }}
                >
                  {i18next.t('noKmlUploaded')}
                </SmallBody>
              </div>
              {uploadButton}
            </Card>
          )}
        </CustomCell>
      );
    },
    constraints: {
      min: { rem: 9 },
      max: { fr: 2 }
    }
  },
  {
    id: 'emissionDate',
    header: '',
    cell: ({ row }) => (
      <CustomCell>
        <FieldLabel>{i18next.t('emissionDate')}</FieldLabel>
        <SmallBody>{row.detail ? utils.date.formatDate(row.detail.emissionDate) : '-'}</SmallBody>
      </CustomCell>
    ),
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  },
  {
    id: 'state',
    header: '',
    cell: ({ row }) => (
      <CustomCell>
        <FieldLabel>{i18next.t('state')}</FieldLabel>
        <SmallBody>{row.detail?.state?.name ?? '-'}</SmallBody>
      </CustomCell>
    ),
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  },
  {
    id: 'totalArea',
    header: '',
    cell: ({ row }) => (
      <CustomCell>
        <FieldLabel>{i18next.t('totalArea')}</FieldLabel>
        <SmallBody>{row.detail?.totalArea ? parseNumberToLocale(Number(row.detail.totalArea)) : '-'}</SmallBody>
      </CustomCell>
    ),
    constraints: {
      min: { rem: 3 },
      max: { fr: 1 }
    }
  },
  {
    id: 'cultivableArea',
    header: '',
    cell: ({ row }) => (
      <CustomCell>
        <FieldLabel>{i18next.t('cultivableArea')}</FieldLabel>
        <SmallBody>
          {row.detail?.cultivableArea ? parseNumberToLocale(Number(row.detail.cultivableArea)) : '-'}
        </SmallBody>
      </CustomCell>
    ),
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  }
];

interface IMatriculaContext {
  agFinancingId: string;
  uploadFilesToPhase: (
    files: File[],
    context: AgFinancingFilesContext,
    documentType?: AgFinancingDocumentType,
    onUploadProgress?: (progress: ProgressDictionary) => void
  ) => Promise<MultipleFileUploadResult>;
}

export const MatriculaContext = React.createContext<IMatriculaContext>({
  agFinancingId: '',
  uploadFilesToPhase: () => Promise.resolve({ fulfilled: [], rejected: [] })
});

const getEvaluationText = (value: string | undefined) => {
  return value ? evaluationTypesOptions.find(e => e.value === value)?.name : '-';
};

const rowDefinition: RowDefinition<FormattedMatriculasDetailData, ColumnId, DefinitionContext, OpenModalAction> = {
  rowId: ({ row }) => String(row.id),
  actions: ({ rowId, ActionButton, onTableAction, row }) => {
    return (
      <>
        <ActionButton
          onClick={() => onTableAction({ type: 'OPEN_UPLOAD_FILE_MODAL', payload: { rowId, isFieldMonitoring: true } })}
        >
          {i18next.t('uploadReport')}
        </ActionButton>
        <ActionButton onClick={() => onTableAction({ type: 'OPEN_DETAIL_MODAL', payload: rowId })}>
          {i18next.t('uploadDetailData')}
        </ActionButton>
      </>
    );
  },
  actionsLocation: 'footer',
  footer: ({ rowId, row, onTableAction }) => {
    return (
      <>
        <VegaIntegrationStatus matriculaId={row.id} />
        {!!row.reports.length && (
          <div className="flex" style={{ overflow: 'auto' }}>
            {row.reports.map(report => (
              <div key={report.id} style={{ marginRight: 10, maxWidth: '400px' }}>
                <DocumentCard
                  document={report}
                  onDelete={() =>
                    onTableAction({
                      type: 'OPEN_DELETE_FILE_MODAL',
                      payload: { rowId, isFieldMonitoring: true, file: report }
                    })
                  }
                />
              </div>
            ))}
          </div>
        )}
      </>
    );
  },
  expandable: ({ row, ColumnSpan }) => {
    const biomeNameValue = row.detail?.biome ? biomeTypesOptions.find(o => o.value === row.detail?.biome)?.name : '-';

    const matriculaDetailItems: GridContainerItem[] = [
      {
        label: i18next.t('farmName'),
        value: row.detail?.farmName
      },
      {
        label: i18next.t('biome'),
        value: biomeNameValue
      },
      {
        label: i18next.t('preservationArea'),
        value: row.detail?.preservationArea ?? '-'
      },
      {
        label: i18next.t('otherArea'),
        value: row.detail?.otherArea ?? '-'
      },
      {
        label: i18next.t('complianceConflictArea'),
        value: row.detail?.complianceConflictArea ?? '-'
      },
      {
        label: i18next.t('ibamaEmbargo'),
        value: getEvaluationText(row.detail?.ibamaEmbargo)
      },
      {
        label: i18next.t('conservationUnities'),
        value: getEvaluationText(row.detail?.conservationUnities)
      },
      {
        label: i18next.t('indigenousLand'),
        value: getEvaluationText(row.detail?.indigenousLand)
      },
      {
        label: i18next.t('ICMBIOEmbargo'),
        value: getEvaluationText(row.detail?.ICMBIOEmbargo)
      },
      {
        label: i18next.t('stateEmbargo'),
        value: getEvaluationText(row.detail?.stateEmbargo)
      },
      {
        label: i18next.t('quilimbolasLand'),
        value: getEvaluationText(row.detail?.quilimbolasLand)
      }
    ];

    return (
      <ColumnSpan>
        {row.detail ? (
          <GridContainer columns={6} items={matriculaDetailItems} classes={{ container: 'mt-3' }} />
        ) : (
          <MediumLightTitle>{i18next.t('noDetailData')}</MediumLightTitle>
        )}
      </ColumnSpan>
    );
  }
};

export const MatriculasList: React.FunctionComponent<MatriculasListProps> = ({ data, fieldMonitoringData }) => {
  /**
   * Context's
   */
  const { agFinancingId } = useContext(FinanceProcessContext);

  /**
   * State
   */
  const [openDeleteFileModal, setOpenDeleteFileModal] = useState(false);
  const [openUploadFileModal, setOpenUploadFileModal] = useState(false);
  const [openUpdateDetailModal, setOpenUpdateDetailModal] = useState(false);
  const [isFieldMonitoring, setIsFieldMonitoring] = useState(false);
  const [selectedFile, setSelectedFile] = useState<AgFinancingFile | undefined>(undefined);
  const [selectedMatricula, setSelectedMatricula] = useState<FormattedMatriculasDetailData | undefined>(undefined);
  const [openSendToIndigo, setOpenSendToIndigo] = useState(false);
  const completedAt = fieldMonitoringData?.completedAt;
  const completedBy = fieldMonitoringData?.completedBy;

  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  const { addFlashMessage } = useFlashMessage();

  const closeDeleteFileModal = (): void => setOpenDeleteFileModal(false);
  const closeUploadModal = (): void => setOpenUploadFileModal(false);
  const closeUpdateDetailModal = (): void => setOpenUpdateDetailModal(false);

  const tableState = useTableState<FormattedMatriculasDetailData, ColumnId, OpenModalAction>(data, {
    onTableAction: action => {
      switch (action.type) {
        case 'OPEN_DELETE_FILE_MODAL':
          setIsFieldMonitoring(action.payload.isFieldMonitoring);
          setSelectedMatricula(data.find(d => d.id === action.payload.rowId));
          setSelectedFile(action.payload.file);
          setOpenDeleteFileModal(true);
          return;
        case 'OPEN_UPLOAD_FILE_MODAL':
          setSelectedMatricula(data.find(d => d.id === action.payload.rowId));
          setIsFieldMonitoring(action.payload.isFieldMonitoring);
          setOpenUploadFileModal(true);
          return;
        case 'OPEN_DETAIL_MODAL':
          setSelectedMatricula(data.find(d => d.id === action.payload));
          setOpenUpdateDetailModal(true);
          return;
        default:
          return;
      }
    }
  });

  const uploadFilesToPhase = uploadFilesToAgFinancing({
    agFinancingId,
    folder: AgFinancingFileFolder.PHASE_2
  });

  const enableSendToIndigo =
    !completedAt && data.some(record => !!record.kml?.id) && !FieldMonitoringUtil.isOnHold(fieldMonitoringData?.status);

  const handleSendToIndigo = () => {
    setOpenSendToIndigo(true);
  };

  const handleCloseSentToIndigo = () => {
    setOpenSendToIndigo(false);
  };

  const sendToIndigoPromise = () => {
    const newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinancingId,
      phase2: {
        fieldMonitoring: {
          status: FieldMonitoringStatus.COMPLETED
        }
      }
    };

    return upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      }
    }).then(() => {
      addFlashMessage({
        content: i18next.t('successSendingAnalysis'),
        title: '',
        variant: 'success'
      });
    });
  };

  return (
    <>
      <div className="flex flex-space-btw" style={{ marginBottom: 32 }}>
        <div>
          <MediumLightTitle>{i18next.t('contractRegistration')}</MediumLightTitle>
          <LegalBody style={{ marginTop: 5 }}>{i18next.t('uploadFileForRegistration')}</LegalBody>
        </div>
        <div className="flex">
          <ToggleOnHoldFieldMonitoringAnalysis />
          <Button size="small" disabled={!enableSendToIndigo} onClick={handleSendToIndigo}>
            {completedAt
              ? `${i18next.t('sentBy')}: ${UserUtil.getUserFullName(completedBy)} ${i18next.t('at')} ${date.formatDate(
                  completedAt,
                  'MMM DD, YYYY'
                )}`
              : i18next.t('sendToIndigo')}
          </Button>
        </div>
      </div>
      {agFinancingId && data?.length ? (
        <>
          <Table label="Matricula" columnDefinition={columnDefinition} rowDefinition={rowDefinition} {...tableState} />
          <MatriculaContext.Provider
            value={{
              agFinancingId,
              uploadFilesToPhase
            }}
          >
            <DeleteFileModal
              open={openDeleteFileModal}
              closeModal={closeDeleteFileModal}
              isFieldMonitoring={isFieldMonitoring}
              selectedFile={selectedFile}
              selectedMatricula={selectedMatricula}
            />
            <UploadFileModal
              open={openUploadFileModal}
              closeModal={closeUploadModal}
              selectedMatricula={selectedMatricula}
              isFieldMonitoring={isFieldMonitoring}
            />

            <UpdateDetailModal
              open={openUpdateDetailModal}
              closeModal={closeUpdateDetailModal}
              selectedMatricula={selectedMatricula}
            />
            <ConfirmationDialog
              open={openSendToIndigo}
              closeDialog={handleCloseSentToIndigo}
              onConfirmPromise={sendToIndigoPromise}
              confirmationText={i18next.t('doYouWantToSendToIndigo')}
              confirmationButtonText={i18next.t('send')}
              dialogMaxWidth="30em"
            />
          </MatriculaContext.Provider>
        </>
      ) : (
        <NoResultsFound />
      )}
    </>
  );
};
